/**
 * Opportunities service — the CRM pursuit pipeline.
 *
 * "Opportunities" are Projects (`tblgWX330MPJkhQVq`) at pre-Executed Contract Stages — the main app
 * hides these (it filters the project list to Contract Stage = 'Executed'), so the CRM owns them here.
 * Reuses services/projects.js for single-record read/write. Returns normalized opportunity objects.
 *
 * No fields[] on the list query (Weighted Fee / fee rollups are lookups that vanish under fields[]).
 */
import { apiGetAll } from './_api.js'
import { getProjectDetails, updateProjectDetails } from './projects.js'
import { logInteraction } from './interactions.js'
import { ensureContactsOnOrganization } from './contacts.js'

const TBL_PROJ = 'tblgWX330MPJkhQVq'

// Early, purely-judgment pursuit stages — no clean system signal to derive these from, so they
// stay freely user-selectable.
export const EARLY_STAGES = ['Business Development', 'Gathering Info', 'Requested', 'Under Review', 'Negotiation', 'Approved']
export const LOST_STAGES = ['No Go by AJA', 'No Go by Prospect', 'Lost']
// Event-driven — set ONLY by the app at the matching real-world moment (proposal posted, client
// signed, firm countersigned). Never offered as a manual pick in the Contract Stage dropdown; see
// CrmOpportunityDetailModal.jsx.
export const SYSTEM_STAGES = ['Proposal Posted', 'Pending Countersignature', 'Executed']
// Everything that still counts as an open, active pursuit (excludes Executed + Lost) — drives the
// pipeline list query AND its stage filter pills. Includes 'Pending Countersignature': it was
// previously missing here, which silently dropped any opportunity sitting in that stage out of the
// whole Opportunities list, not just the dropdown.
export const PURSUIT_STAGES = [...EARLY_STAGES, 'Proposal Posted', 'Pending Countersignature']
// What the Contract Stage dropdown may actually be set to by hand.
export const MANUAL_STAGES = [...EARLY_STAGES, ...LOST_STAGES]
export const ALL_STAGES = [...PURSUIT_STAGES, 'Executed', ...LOST_STAGES]

export const LOSS_REASONS = [
  'Budget Mismatch', 'Timeline Mismatch', 'Chose a Competitor', 'Project Cancelled',
  'Client Went Unresponsive', 'Not a Good Fit', 'Fee Too High', 'Other',
]

// Probability of close by stage (drives the weighted pipeline). From the legacy opportunities list.
export const STAGE_PROB = {
  'Business Development': 0.05, 'Gathering Info': 0.10, 'Requested': 0.15,
  'Under Review': 0.35, 'Negotiation': 0.60, 'Approved': 0.80, 'Proposal Posted': 0.25,
  'Pending Countersignature': 0.90,
}

/* ── Fit assessment (from the legacy opportunity detail tool) ────────────────────────────────
 * Three manual 5-point ratings the team sets per opportunity — none derived, all judgment calls:
 *   Complexity Rating (Projects)      — how hard the site/project itself is to execute
 *   Portfolio Rating   (Projects)     — how the project reads in our portfolio/marketing
 *   Client Rating      (Organizations, NOT Projects — a schema quirk: it lives on the linked
 *                        Client organization, since "how easy to work with" is a property of the
 *                        client relationship, not any one project)
 * Option arrays mirror the live Airtable singleSelect choice order exactly — do not reorder.
 */
export const COMPLEXITY_OPTS   = ['Very Simple', 'Simple', 'Standard', 'Complex', 'Very Complex']
export const PORTFOLIO_OPTS    = ['Very Negative Impact', 'Negative Impact', 'Neutral Impact', 'Positive Impact', 'Very Positive Impact']
export const CLIENT_STYLE_OPTS = ['Very Easy', 'Easy', 'Standard', 'Challenging', 'Very Challenging']

const FIT_COLORS = {
  'Very Simple': '#1D9E75', 'Simple': '#1D9E75', 'Standard': '#BA7517',
  'Positive Impact': '#1D9E75', 'Very Positive Impact': '#1D9E75', 'Neutral Impact': '#BA7517',
  'Negative Impact': '#E24B4A', 'Very Negative Impact': '#E24B4A',
  'Complex': '#E24B4A', 'Very Complex': '#E24B4A',
  'Easy': '#1D9E75', 'Very Easy': '#1D9E75',
  'Challenging': '#E24B4A', 'Very Challenging': '#E24B4A',
}
export function fitColor(val) { return FIT_COLORS[val] || null }

function num(v) { const n = Number(v); return isNaN(n) ? 0 : n }

function normOpp(r) {
  const f = (r && r.fields) || {}
  const first = k => (Array.isArray(f[k]) ? f[k][0] : null) || null
  const stage = f['Contract Stage'] || ''
  const dc = f['Executed DC Proposal'] || []
  const bs = f['Executed BS Proposal'] || []
  const hasDC = Array.isArray(dc) ? dc.length > 0 : !!dc
  const hasBS = Array.isArray(bs) ? bs.length > 0 : !!bs
  const fee = num(f['Weighted Fee Amount (from Fee Proposals)'])
  const weighted = LOST_STAGES.indexOf(stage) !== -1 ? 0
    : (f['Weighted Fee'] ? num(f['Weighted Fee']) : fee * (STAGE_PROB[stage] || 0.10))
  return {
    id: r.id,
    name: f['Project Name'] || f['Name'] || '(unnamed)',
    stage,
    displayStage: (stage === 'Executed' && hasDC && !hasBS) ? 'Consultation Active' : stage,
    projectType: f['Project Type'] || f['Type of Project'] || '',
    address: f['Project Street Address'] || '',
    state: f['Project State'] || '',
    estValue: num(f['Estimated Construction Value'] || f['Project Value']) || null,
    fee, weighted,
    prob: STAGE_PROB[stage] != null ? STAGE_PROB[stage] : null,
    proposalDate: f['Proposal Date'] || f['Proposal Posted Date'] || null,
    createdTime: f['Created'] || null,
    leadId: first('Leads'),
    primaryContactId: first('Primary Contact') || first('Project Contact'),
    raw: f,
  }
}

function listByStages(stages) {
  const formula = 'OR(' + stages.map(s => `{Contract Stage}='${s}'`).join(',') + ')'
  return apiGetAll(TBL_PROJ, 'filterByFormula=' + encodeURIComponent(formula)).then(recs => recs.map(normOpp))
}

/** Active pursuit pipeline (pre-Executed, non-lost). */
export function getOpportunities() { return listByStages(PURSUIT_STAGES) }

/** Lost / no-go opportunities (for a Lost view + win-rate). */
export function getLostOpportunities() { return listByStages(LOST_STAGES) }

/**
 * Search projects by name across ALL contract stages (incl. Executed) — so mid-project work like
 * ASRs can reach a project that has left the pursuit pipeline.
 */
export async function searchAllOpportunities(q) {
  const safe = String(q || '').replace(/["\\]/g, '').toLowerCase()
  if (!safe.trim()) return []
  const formula = `FIND("${safe}",LOWER({Project Name}))`
  const recs = await apiGetAll(TBL_PROJ, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=25')
  return recs.map(normOpp)
}

/** Full detail for one opportunity. */
export async function getOpportunityDetail(projectId) {
  const r = await getProjectDetails(projectId)
  return r ? normOpp(r) : null
}

async function clerkToken() {
  try { return typeof window.__clerkGetToken === 'function' ? await window.__clerkGetToken().catch(() => null) : null } catch { return null }
}

/**
 * Best-effort log to database/116_opportunity_stage_history.sql (api/opportunity-stage-history.js)
 * — never blocks or throws into the caller; a missed history row is far less costly than a failed
 * stage change. Feeds the Tier 2 pipeline analytics (win rate, sales-cycle length, stage funnel).
 */
async function logStageChange(projectId, fromStage, toStage, { reason, source } = {}) {
  try {
    const token = await clerkToken()
    await fetch('/api/opportunity-stage-history', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
      body: JSON.stringify({ action: 'log', projectId, fromStage, toStage, reason, source: source || 'manual' }),
    })
  } catch (_) { /* best-effort */ }
}

/** Move an opportunity to a different Contract Stage. `fromStage` (the stage before this call) is
 * only used for the history log — pass the caller's current stage state. */
export async function updateContractStage(projectId, stage, fromStage) {
  const res = await updateProjectDetails(projectId, { 'Contract Stage': stage })
  logStageChange(projectId, fromStage, stage, { source: 'manual' })
  return res
}

/**
 * Native replacement for the retired Airtable automation that advanced Contract Stage to
 * 'Proposal Posted' when a proposal entered its Posted Proposals view. Called right after
 * markPosted() in the CRM's post flow (CrmProposalDetailModal.jsx). Non-clobbering: only
 * advances from a manual pursuit stage (MANUAL_STAGES) — never stomps a stage a system event
 * (or the team) has already moved past, mirroring the guard pattern in journey-respond.js /
 * proposal-pdf.js's own stage advances.
 */
export async function advanceToProposalPosted(projectId) {
  const proj = await getProjectDetails(projectId)
  const current = proj && proj.fields && proj.fields['Contract Stage']
  if (current && !MANUAL_STAGES.includes(current)) return
  await updateProjectDetails(projectId, { 'Contract Stage': 'Proposal Posted' })
  logStageChange(projectId, current, 'Proposal Posted', { source: 'system:posted' })
}

/**
 * Move an opportunity to a Lost/No-Go stage with a required reason — mirrors services/leads.js's
 * disqualifyLead. The Contract Stage write always succeeds (existing field); the Loss Reason write
 * is best-effort — the field doesn't exist in Airtable yet (schema-write access is blocked; see
 * docs/CRM.md for the exact field to add by hand). Either way the reason is never lost: it's also
 * logged as a Decision (Lost) interaction, which works today with no schema change.
 */
export async function moveToLostStage(projectId, stage, reason, notes, fromStage) {
  await updateProjectDetails(projectId, { 'Contract Stage': stage })
  try { await updateProjectDetails(projectId, { 'Loss Reason': reason || 'Other' }) } catch (_) { /* field not created yet */ }
  await logInteraction({
    projectId, type: 'Action Taken', opportunityType: 'Decision (Lost)',
    notes: `${stage}. Reason: ${reason || 'Other'}.${notes ? ' ' + notes : ''}`,
  })
  logStageChange(projectId, fromStage, stage, { reason: reason || 'Other', source: 'system:lost' })
}

/**
 * The opportunity's own contacts (Projects.Contacts, filled at conversion) belong to its client
 * organization: add any that aren't, and make the first the organization's Primary Contact when it
 * has none. Run when Client Hub access opens and when the client organization is set, so nobody
 * has to remember to go back to the organization card. Returns true when anything changed.
 */
export async function syncOpportunityContactsToClient(projectId) {
  if (!projectId) return false
  const p = await getProjectDetails(projectId)
  const f = (p && p.fields) || {}
  const orgId = (f['Client'] || [])[0]
  const contactIds = [...(f['Contacts'] || []), ...(f['Primary Contact'] || []), ...(f['Project Contact'] || [])]
  return ensureContactsOnOrganization(orgId, [...new Set(contactIds)])
}
